<div class="legal-shell">

  <header class="legal-title">
    <span class="ls-eyebrow">Cadre juridique · LyricStream SAS</span>
    <h1>{{ 'CONDITIONS.TITLE' | translate }}</h1>
    <div class="meta">
      <span>Version <b>4.2</b></span>
      <span class="dot"></span>
      <span>En vigueur depuis le <b>1<sup>er</sup> mai 2026</b></span>
      <span class="dot"></span>
      <a href="#">Historique des versions</a>
    </div>
    <p class="lede">
      Bienvenue sur LyricStream. Ces conditions encadrent l'usage de la plateforme — par toi, par la communauté,
      par notre équipe éditoriale. On a essayé de les écrire en français clair, sans jargon inutile.
      Si quelque chose n'est pas clair, écris-nous&nbsp;:
      <a href="mailto:legal@lyricstream.app">legal@lyricstream.app</a>.
    </p>
  </header>

  <aside class="toc">
    <h2>{{ 'CONDITIONS.SOMMAIRE' | translate }}</h2>
    <ol>
      @for (section of sections; track section.id; let i = $index) {
        <li [class.on]="activeSection() === section.id">
          <a [routerLink]="['.']" [fragment]="section.id" (click)="goTo(section.id, $event)">{{ section.label }}</a>
        </li>
      }
    </ol>
  </aside>

  <main class="legal-body">

    <h2 id="a1"><span class="num">Article 1</span>Objet du service</h2>
    <p>LyricStream est une <b>bibliothèque collaborative en ligne</b> de chants chrétiens — paroles, accords, partitions, enregistrements audio quand ils existent. Elle est éditée par <b>LyricStream SAS</b>, dont le siège social est à Port-au-Prince, Haïti (RCCAM n° 2023-PAP-04217).</p>
    <p>Le service est gratuit pour la consultation. Certaines fonctions avancées (téléchargement hors connexion, sélections éditoriales offline, statistiques détaillées) sont réservées aux abonnés <a [routerLink]="['/', lang(), 'premium']">Premium</a>.</p>
    <div class="callout">
      <b>En clair&nbsp;:</b> tu peux chercher, lire, projeter, et contribuer gratuitement. Les fonctions "pour les pros" sont payantes mais le cœur reste ouvert à tous.
    </div>

    <h2 id="a2"><span class="num">Article 2</span>Acceptation des conditions</h2>
    <p>En créant un compte, en contribuant, ou simplement en naviguant sur LyricStream, tu reconnais avoir lu et accepté les présentes conditions. Si tu refuses tout ou partie de ces conditions, tu dois <b>cesser d'utiliser le service</b>.</p>
    <p>Pour les utilisateurs de moins de 15 ans, l'usage du service nécessite l'accord d'un parent ou tuteur légal.</p>

    <h2 id="a3"><span class="num">Article 3</span>Compte utilisateur</h2>
    <h3>Création</h3>
    <p>La création d'un compte nécessite une adresse e-mail valide. Tu peux choisir entre un mot de passe ou un <b>lien magique</b> envoyé par e-mail à chaque connexion.</p>
    <h3>Sécurité</h3>
    <p>Tu es responsable de la confidentialité de tes identifiants. Préviens-nous immédiatement à <a href="mailto:security@lyricstream.app">security&#64;lyricstream.app</a> en cas de compromission.</p>
    <h3>Suppression</h3>
    <p>Tu peux supprimer ton compte à tout moment depuis Paramètres → Compte. Tes contributions publiées restent dans la bibliothèque sous leur attribution, sauf demande explicite de retrait justifiée.</p>

    <h2 id="a4"><span class="num">Article 4</span>Contributions de la communauté</h2>
    <p>LyricStream vit par sa communauté. Quand tu ajoutes ou modifies un chant, tu <b>certifies</b>&nbsp;:</p>
    <ul>
      <li>Que les paroles sont dans le <b>domaine public</b>, ou que tu en détiens les droits, ou que ton usage relève d'une exception légale (citation, étude, culte).</li>
      <li>Que les enregistrements audio que tu joins ont été <b>réalisés par toi</b> ou avec l'autorisation explicite de leur titulaire.</li>
      <li>Que ton contenu ne contient pas d'injure, de propos haineux, de désinformation flagrante.</li>
    </ul>
    <p>En soumettant une contribution, tu accordes à LyricStream une licence <b>non-exclusive, mondiale, gratuite</b> pour la diffuser, la stocker, la modérer, la traduire, et la rendre interopérable avec nos modes Projection et Karaoké.</p>

    <h2 id="a5"><span class="num">Article 5</span>Droits d'auteur et signalement</h2>
    <p>LyricStream respecte la propriété intellectuelle. Si tu estimes qu'un contenu publié sur la plateforme enfreint tes droits, écris-nous à <a href="mailto:copyright@lyricstream.app">copyright&#64;lyricstream.app</a> avec&nbsp;:</p>
    <ol>
      <li>L'identification précise du contenu litigieux (URL, titre, numéro de recueil).</li>
      <li>Une preuve raisonnable de tes droits (dépôt SACEM, contrat d'édition, etc.).</li>
      <li>Tes coordonnées complètes.</li>
    </ol>
    <p>Nous nous engageons à examiner ta demande sous 7 jours ouvrés et à retirer le contenu si la réclamation est fondée.</p>
    <div class="callout">
      <b>Domaine public&nbsp;:</b> les hymnes antérieurs à 1928 sont en principe libres. Pour les œuvres récentes, nous travaillons avec la SACEM, BMI, et les éditeurs spécialisés (Hillsong Music, Integrity, etc.).
    </div>

    <h2 id="a6"><span class="num">Article 6</span>Usage acceptable</h2>
    <p>Sont strictement interdits&nbsp;:</p>
    <ul>
      <li>Toute revente ou redistribution commerciale du contenu sans accord écrit.</li>
      <li>L'usage de bots ou de scrapers pour extraire massivement le contenu.</li>
      <li>La manipulation des charts par fausses écoutes ou faux comptes.</li>
      <li>L'ajout de contenu à caractère commercial déguisé en chant (publicité, prosélytisme agressif).</li>
      <li>Tout contenu incitant à la haine raciale, religieuse, ou aux discriminations.</li>
    </ul>

    <h2 id="a7"><span class="num">Article 7</span>Abonnement Premium</h2>
    <p>L'abonnement Premium offre, en plus du gratuit&nbsp;: téléchargement hors connexion, sélections éditoriales offline, transposition automatique avancée, statistiques détaillées, support prioritaire.</p>
    <table>
      <thead>
        <tr><th>Formule</th><th>Prix</th><th>Durée</th></tr>
      </thead>
      <tbody>
        <tr><td>Mensuelle</td><td><b>4,99&nbsp;€</b> / mois</td><td>Renouvellement automatique</td></tr>
        <tr><td>Annuelle</td><td><b>49&nbsp;€</b> / an (–18&nbsp;%)</td><td>Renouvellement automatique</td></tr>
        <tr><td>Paroisses (10+ comptes)</td><td>Sur devis</td><td>Annuel · facturation centralisée</td></tr>
      </tbody>
    </table>
    <p>Tu peux <b>résilier à tout moment</b> depuis Paramètres → Abonnement. La résiliation prend effet à la fin de la période en cours, sans remboursement au prorata sauf cas exceptionnel.</p>

    <h2 id="a8"><span class="num">Article 8</span>Modération</h2>
    <p>Toute contribution est soumise à modération par notre équipe avant publication. Les délais habituels sont de 24 à 72 heures.</p>
    <p>Nous nous réservons le droit de refuser, modifier ou retirer toute contribution qui ne respecterait pas ces conditions, sans avoir à nous justifier. Les retraits motivés par un signalement légitime sont prioritaires.</p>

    <h2 id="a9"><span class="num">Article 9</span>Limitation de responsabilité</h2>
    <p>LyricStream est fourni "en l'état". Nous mettons tout en œuvre pour assurer la qualité du contenu et la continuité du service, mais nous ne garantissons pas&nbsp;:</p>
    <ul>
      <li>L'exactitude absolue de chaque paroles, accord ou traduction (vérifie toujours pour un usage liturgique officiel).</li>
      <li>La disponibilité 24/7 (maintenance, incidents serveur, panne réseau).</li>
      <li>La compatibilité avec tous les navigateurs et appareils anciens.</li>
    </ul>
    <p>Notre responsabilité financière est plafonnée au montant payé par l'utilisateur sur les 12 derniers mois, sauf en cas de faute lourde.</p>

    <h2 id="a10"><span class="num">Article 10</span>Modification des conditions</h2>
    <p>Nous pouvons faire évoluer ces conditions. Les utilisateurs sont prévenus par e-mail au moins <b>30 jours</b> avant l'entrée en vigueur d'une modification substantielle. L'usage continu du service après cette date vaut acceptation.</p>

    <h2 id="a11"><span class="num">Article 11</span>Droit applicable et litiges</h2>
    <p>Les présentes conditions sont régies par le <b>droit haïtien</b> pour les utilisateurs basés en Haïti, et par le <b>droit français</b> pour les utilisateurs européens. Tout litige sera porté, à défaut d'accord amiable, devant les tribunaux compétents du siège social de LyricStream SAS.</p>
    <p>Avant tout recours contentieux, tu peux saisir notre service médiation à <a href="mailto:mediation@lyricstream.app">mediation&#64;lyricstream.app</a>.</p>

    <div class="callout" style="margin-top:32px;">
      <b>Une question, une remarque&nbsp;?</b> Écris-nous à <a href="mailto:hello@lyricstream.app">hello&#64;lyricstream.app</a>.
      La traduction krèyol de ces conditions est disponible <a [routerLink]="['/', 'ht', 'conditions']">ici</a>.
    </div>

  </main>

  <footer class="legal-foot">
    <span>&copy; 2022–{{ year }} LyricStream SAS · Port-au-Prince</span>
    <div class="nav-pair">
      <a [routerLink]="['/', lang(), 'confidentialite']">Confidentialité →</a>
    </div>
  </footer>

</div>
